/**
 * This file sets up the Redux store for the application using Redux Toolkit's configureStore.
 * It combines all feature reducers (auth, rewards, etc.) and exports the store instance.
 * Also exports custom hooks for dispatching actions and selecting state.
 */
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import authReducer, { initialState as authInitialState } from '../../features/auth/store/authSlice';
import rewardsReducer, { initialState as rewardsInitialState } from '../../features/rewards/store/rewardsSlice';
import scanReducer, { initialState as scanInitialState } from '../../features/scan/store/scanSlice';
import settingsReducer, { initialState as settingsInitialState } from '../../features/settings/store/settingsSlice';
import generateReducer, { initialState as generateInitialState } from '../../features/generate/store/generateSlice';
import customersReducer, {
    initialState as customersInitialState,
} from '../../features/customers/store/customersSlice';
import tutorialReducer, {
    initialState as tutorialInitialState,
} from '../../features/tutorial/store/tutorialSlice';
import { createLogger } from '../config/logger';
import { setTokenGetter } from '../services/api/client';
import { createTokenGetter } from './middleware/apiMiddleware';

const logger = createLogger('store.js');
// Create a root reducer that can handle logout
const appReducer = combineReducers({
    auth: authReducer, // Handles authentication state
    rewards: rewardsReducer, // Handles rewards state
    scan: scanReducer,
    settings: settingsReducer,
    generate: generateReducer, // Add generate slice
    customers: customersReducer,
    tutorial: tutorialReducer, // Tutorial state
    // Add other reducers here as needed
});

// Root reducer that handles logout by resetting all slices
const rootReducer = (state, action) => {
    if (action.type === 'LOGOUT') {
        logger.info('Logout action received, resetting all slices');
        // Reset all slices. Auth must have loading: false so we don't show "Loading..." after logout.
        return {
            auth: { ...authInitialState, loading: false },
            rewards: rewardsInitialState,
            scan: scanInitialState,
            generate: generateInitialState,
            settings: settingsInitialState,
            customers: customersInitialState,
            tutorial: tutorialInitialState,
        };
    }
    return appReducer(state, action);
};

// Configuring the Redux store with the root reducer
export const store = configureStore({
    reducer: rootReducer,
});

// Set token getter for axios interceptor
// This replaces the global store injection pattern
setTokenGetter(createTokenGetter(store.getState.bind(store)));

export default store;

// Re-export hooks from hooks.js for backward compatibility
export { useAppDispatch, useAppSelector } from './hooks';
